import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import EffectiveDateField, { isValidYmd } from "./EffectiveDateField";
import styles from "./SaveEffectiveDateModal.module.css";

type SaveEffectiveDateModalProps = {
  show: boolean;
  title?: string;
  changedFieldLabels: string[];
  saving?: boolean;
  errorMessage?: string;
  onCancel: () => void;
  onConfirm: (effectiveStartDate: string | null) => void;
};

const SaveEffectiveDateModal = ({
  show,
  title = "Confirm Changes",
  changedFieldLabels,
  saving = false,
  errorMessage,
  onCancel,
  onConfirm,
}: SaveEffectiveDateModalProps) => {
  const [mode, setMode] = useState<"now" | "future">("now");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (!show) return;
    setMode("now");
    setEffectiveDate("");
    setDateError("");
  }, [show]);

  const handleConfirm = () => {
    if (mode === "future") {
      if (!effectiveDate) {
        setDateError("Select an effective date or choose Apply now.");
        return;
      }
      if (!isValidYmd(effectiveDate)) {
        setDateError("Use a valid effective date.");
        return;
      }
      onConfirm(effectiveDate);
      return;
    }

    onConfirm(null);
  };

  const titleId = "saveEffectiveDateTitle";
  const errorId = "saveEffectiveDateError";

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onCancel}
      centered
      backdrop="static"
      dialogClassName={styles["save-effective-date-modal"]}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="save-effective-date-modal"
    >
      <Modal.Header closeButton={!saving} className={styles["save-effective-date-header"]}>
        <Modal.Title id={titleId} className={styles["save-effective-date-title"]}>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles["save-effective-date-body"]}>
        <p className={styles["save-effective-date-copy"]}>
          The following report-affecting fields changed:
        </p>
        <ul className={styles["save-effective-date-list"]} data-testid="save-effective-date-changed-list">
          {changedFieldLabels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>

        <fieldset className={styles["save-effective-date-fieldset"]}>
          <legend className={styles["save-effective-date-legend"]}>
            When should this change apply?
          </legend>
          <label className={styles["save-effective-date-radio"]}>
            <input
              type="radio"
              name="effectiveDateMode"
              value="now"
              checked={mode === "now"}
              onChange={() => setMode("now")}
              disabled={saving}
              data-testid="save-effective-date-mode-now"
            />
            Apply now (effective today)
          </label>
          <label className={styles["save-effective-date-radio"]}>
            <input
              type="radio"
              name="effectiveDateMode"
              value="future"
              checked={mode === "future"}
              onChange={() => setMode("future")}
              disabled={saving}
              data-testid="save-effective-date-mode-future"
            />
            Apply on a selected effective date
          </label>
        </fieldset>

        {mode === "future" && (
          <EffectiveDateField
            id="saveEffectiveDate"
            value={effectiveDate}
            onChange={(value) => {
              setEffectiveDate(value);
              setDateError("");
            }}
            disabled={saving}
            data-testid="save-effective-date-input"
          />
        )}

        {(dateError || errorMessage) && (
          <div id={errorId} className={styles["save-effective-date-error"]} role="alert">
            {dateError || errorMessage}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={styles["save-effective-date-footer"]}>
        <button
          type="button"
          className={styles["save-effective-date-btn-secondary"]}
          onClick={onCancel}
          disabled={saving}
          data-testid="save-effective-date-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles["save-effective-date-btn"]}
          onClick={handleConfirm}
          disabled={saving}
          aria-describedby={dateError || errorMessage ? errorId : undefined}
          data-testid="save-effective-date-confirm"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveEffectiveDateModal;
